

import React, { useState, useEffect } from 'react';
// Corrected import path for types.ts
import { User, UserRole } from '@/types';
import { supabase } from "@/supabaseClient";
import { ShieldCheck, AlertTriangle, Loader2, ArrowRight, LockKeyhole, Mail, ArrowLeft, CheckCircle2, Wrench } from 'lucide-react';
import { DEFAULT_CONFIG } from '@/config';

export interface LoginProps {
  onLogin: (user: User) => void;
  appLogo?: string;
  backgroundImage?: string;
  isMaintenanceMode: boolean; // New prop
  maintenanceMessage: string; // New prop
  appName?: string; // Add appName prop
  settingsUpdatedAt?: number; // New: Timestamp for settings update
}

export const Login: React.FC<LoginProps> = ({ onLogin, appLogo, backgroundImage, isMaintenanceMode, maintenanceMessage, appName, settingsUpdatedAt }) => {
  // Login Form State
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  
  // Password Reset State
  const [isResetMode, setIsResetMode] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);

  // REMOVIDO: Estados `bgLoadError` e `logoLoadError` e seus `useEffect`s.
  // A re-tentativa de carregamento agora é gerenciada unicamente pela propriedade `key`.
  // A `onError` permanecerá apenas para logging no console, sem alterar o `display`.

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
        const { data: authData, error: authError } = await supabase.auth.signInWithPassword({
          email,
          password
        });

        if (authError) {
          throw authError;
        }

        const supabaseUser = authData.user;
        if (!supabaseUser) {
          throw new Error("Falha na autenticação.");
        }

        // 2. Get User Profile
        const { data: userData, error: profileError } = await supabase
          .from('users')
          .select('*')
          .eq('id', supabaseUser.id)
          .single();

        if (profileError) {
          throw new Error('Falha ao obter perfil de usuário: ' + profileError.message);
        }

        if (userData) {
          const appUser: User = {
            id: userData.id,
            fullName: userData.full_name,
            username: userData.username,
            email: userData.email,
            sector: userData.sector,
            role: userData.role,
            avatar: userData.avatar || undefined,
            mustChangePassword: userData.must_change_password
          };
          
          // --- NOVO: Lógica de Modo de Manutenção ---
          if (isMaintenanceMode && appUser.role !== UserRole.ADMIN) {
            setError('Sistema em manutenção. Somente administradores podem acessar no momento.');
            await supabase.auth.signOut(); // Desloga o usuário imediatamente
            setIsLoading(false); // Garante que o loading state seja resetado
            return; // Impede que o login prossiga
          }
          // --- FIM NOVO ---

          onLogin(appUser);
        } else {
          setError('Usuário autenticado, mas sem perfil. Entre em contato com o administrador.');
        }
    } catch (err: any) {
      console.error("Auth error:", err);
      
      if (err.status === 400 || err.message?.includes('Invalid login credentials') || err.code === 'invalid_credentials') {
        setError('Email ou senha incorretos.');
      } else if (err.code === 'user_disabled') {
        setError('Esta conta foi desativada pelo administrador.');
      } else if (err.status === 429) {
        setError('Muitas tentativas falhas. Tente novamente mais tarde.');
      } else if (err.message) {
        setError(err.message);
      } else {
        setError('Erro ao conectar. Verifique sua internet.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordReset = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) {
      setError("Digite seu e-mail para recuperar a senha.");
      return;
    }

    setIsLoading(true);
    setError('');
    setResetSuccess(false);

    try {
      const { error: resetError } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      if (resetError) throw resetError;
      setResetSuccess(true);
    } catch (err: any) {
      console.error("Reset error:", err);
      if (err.status === 404 || err.message?.includes("User not found")) {
        setError("Este e-mail não está cadastrado no sistema.");
      } else if (err.status === 400 || err.message?.includes("validation")) {
        setError("Formato de e-mail inválido.");
      } else {
        setError("Erro ao enviar e-mail. Tente novamente.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const currentBgUrl = backgroundImage || DEFAULT_CONFIG.loginBackgroundImageUrl;
  const currentLogoUrl = appLogo || DEFAULT_CONFIG.logoUrl;
  const displayAppName = (appName || DEFAULT_CONFIG.appName || 'SÓES Wiki').replace(/s[óo]\s+escrit[óo]rio/gi, 'SÓES'); // Usado para o fallback de texto

  return (
    <div className="min-h-screen flex w-full bg-white font-sans">
      
      {/* Left Side - Visual Branding */}
      <div className="hidden lg:flex lg:w-1/2 relative bg-slate-900 overflow-hidden flex-col justify-between p-12 text-white">
        {/* Dynamic Background Image */}
        <div className="absolute inset-0 z-0">
           {/* Usando key para forçar re-renderização e re-tentativa de carregamento */}
           {currentBgUrl ? (
             <img 
               key={`bg-${currentBgUrl}-${settingsUpdatedAt || DEFAULT_CONFIG.lastUpdatedAt}`} // Key com timestamp
               src={currentBgUrl} 
               alt="Office Background" 
               className="w-full h-full object-cover opacity-40 transition-opacity duration-700"
               onError={(e) => { 
                 console.error('Login: Background image failed to load:', currentBgUrl, e); 
                 // REMOVIDO: e.currentTarget.style.display = 'none';
               }}
             />
           ) : (
             // Fallback Pattern if image fails or is missing
             <div className="w-full h-full bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10"></div>
           )}
           <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/40 to-slate-900/10" />
        </div>
        
        {/* LOGO */}
        <div className="relative z-10">
           {currentLogoUrl ? (
             <img 
                key={`logo-${currentLogoUrl}-${settingsUpdatedAt || DEFAULT_CONFIG.lastUpdatedAt}`} // Key com timestamp
                src={currentLogoUrl} 
                alt="Logo" 
                className="h-12 md:h-14 object-contain mb-6 drop-shadow-md" 
                onError={(e) => { 
                  console.error('Login: Logo image failed to load:', currentLogoUrl, e); 
                  // REMOVIDO: e.currentTarget.style.display = 'none';
                }}
             />
           ) : (
             <div className="flex items-center gap-3 mb-6 animate-fade-in">
               <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center font-bold text-xs shadow-lg shadow-blue-900/50">SÓES</div>
               <h1 className="font-bold text-2xl tracking-tight">{displayAppName}</h1> {/* Usa displayAppName */}
             </div>
           )}
        </div>

        <div className="relative z-10 max-w-lg">
          <blockquote className="text-2xl font-medium leading-relaxed mb-6">
            "A organização do conhecimento é a chave para a eficiência operacional."
          </blockquote>
          <div className="flex items-center gap-4">
            <div className="h-1 w-12 bg-blue-500 rounded-full"></div>
            <p className="text-sm text-slate-400 uppercase tracking-widest font-semibold">{displayAppName} V2.0</p>
          </div>
        </div>

        <div className="relative z-10 text-xs text-slate-500 flex gap-6">
           <span>&copy; {new Date().getFullYear()} SÓES</span>
        </div>
      </div>

      {/* Right Side - Login Form / Maintenance Message */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-6 bg-slate-50">
        <div className="w-full max-w-md bg-white p-8 rounded-2xl shadow-sm border border-slate-200">
          
          {/* Mobile Logo Fallback */}
          <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center text-blue-600 mb-6 lg:hidden">
              <ShieldCheck size={24} />
          </div>

          {isMaintenanceMode && (
            <div className="bg-red-50 border border-red-100 text-red-700 p-4 rounded-xl flex flex-col items-center gap-2 mb-6 animate-fade-in text-center">
              <Wrench className="w-5 h-5 flex-shrink-0" />
              <div className="text-center">
                <p className="font-bold text-lg mb-1">Sistema em Manutenção</p>
                <p className="text-base text-blue-700 leading-relaxed">{maintenanceMessage}</p>
                <p className="text-sm mt-2 font-medium text-slate-700">{'Apenas administradores podem acessar.'}</p>
              </div>
            </div>
          )}

          {!isResetMode ? (
            <>
              <div className="mb-8">
                <h2 className="text-3xl font-bold text-slate-900 mb-2">Bem-vindo de volta</h2>
                <p className="text-slate-500">Insira suas credenciais corporativas para acessar.</p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">Email Corporativo</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:bg-white focus:border-transparent transition-all outline-none"
                    placeholder="nome@soescritorio.com.br"
                    required
                  />
                </div>
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <label className="block text-sm font-semibold text-slate-700">Senha</label>
                    <button 
                      type="button"
                      onClick={() => {
                         setIsResetMode(true);
                         setError('');
                      }} 
                      className="text-xs font-medium text-blue-600 hover:text-blue-700 hover:underline outline-none"
                    >
                      Esqueceu a senha?
                    </button>
                  </div>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:bg-white focus:border-transparent transition-all outline-none"
                    placeholder="••••••••"
                    required
                  />
                </div>
                
                {error && (
                  <div className={`flex items-start gap-3 text-sm p-4 rounded-xl border bg-red-50 text-red-700 border-red-100 animate-fade-in`}>
                    <AlertTriangle className="w-5 h-5 flex-shrink-0" />
                    <span>{error}</span>
                  </div>
                )}
                
                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full font-bold py-3.5 px-4 rounded-xl transition-all bg-blue-600 hover:bg-blue-700 text-white flex justify-center items-center gap-2 shadow-lg shadow-blue-900/20 hover:shadow-blue-900/30 hover:-translate-y-0.5"
                >
                  {isLoading ? (
                    <><Loader2 className="w-5 h-5 animate-spin"/> Verificando...</>
                  ) : (
                    <>Acessar Plataforma <ArrowRight className="w-5 h-5" /></>
                  )}
                </button>
              </form>
            </>
          ) : (
            <div className="animate-fade-in">
              <button 
                 onClick={() => {
                   setIsResetMode(false);
                   setError('');
                   setResetSuccess(false);
                 }}
                 className="flex items-center gap-2 text-slate-500 hover:text-slate-700 text-sm font-medium mb-6 transition-colors"
              >
                <ArrowLeft className="w-4 h-4" /> Voltar para login
              </button>

              <div className="mb-6">
                <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center text-blue-600 mb-4">
                  <Mail size={24} />
                </div>
                <h2 className="text-2xl font-bold text-slate-900 mb-2">Recuperar Senha</h2>
                <p className="text-slate-500 text-sm">
                  Digite seu e-mail corporativo abaixo. Enviaremos um link seguro para você criar uma nova senha.
                </p>
              </div>

              {resetSuccess ? (
                <div className="bg-green-50 border border-green-200 rounded-xl p-6 text-center animate-in zoom-in-95">
                   <div className="flex justify-center mb-3">
                     <CheckCircle2 className="w-12 h-12 text-green-500" />
                   </div>
                   <h3 className="font-bold text-green-800 mb-1">E-mail Enviado!</h3>
                   <p className="text-green-700 text-sm mb-4">
                     Verifique sua caixa de entrada (e spam) para redefinir sua senha.
                   </p>
                   <button
                     onClick={() => setIsResetMode(false)}
                     className="text-sm font-medium text-green-800 underline hover:text-green-900"
                   >
                     Voltar para Login
                   </button>
                </div>
              ) : (
                <form onSubmit={handlePasswordReset} className="space-y-5">
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">Email Corporativo</label>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:bg-white focus:border-transparent transition-all outline-none"
                      placeholder="nome@soescritorio.com.br"
                      required
                    />
                  </div>

                  {error && (
                    <div className="flex items-start gap-3 text-sm p-4 rounded-xl border bg-red-50 text-red-700 border-red-100 animate-fade-in">
                      <AlertTriangle className="w-5 h-5 flex-shrink-0" />
                      <span>{error}</span>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={isLoading}
                    className="w-full font-bold py-3.5 px-4 rounded-xl transition-all bg-blue-600 hover:bg-blue-700 text-white flex justify-center items-center gap-2 shadow-lg shadow-blue-900/20"
                  >
                    {isLoading ? (
                      <><Loader2 className="w-5 h-5 animate-spin"/> Enviando...</>
                    ) : (
                      <>Enviar Link de Recuperação</>
                    )}
                  </button>
                </form>
              )}
            </div>
          )}

          <div className="mt-8 pt-6 border-t border-slate-100 flex items-center justify-center gap-2 text-xs text-slate-400">
             <LockKeyhole className="w-3 h-3" />
             <span>Ambiente Seguro & Criptografado</span>
          </div>
        </div>
      </div>
    </div>
  );
};